import Bugsnag from '@bugsnag/js';
import { ReactComponent as ClearIcon } from 'assets/icons/icon-close.svg';
import { ReactComponent as OfficeIcon } from 'assets/icons/icon-office.svg';
import Button from 'components/Button';
import CardWithAside from 'components/Card/CardWithAside';
import CardFooter from 'components/CardFooter/CardFooter';
import { makeMutationForm } from 'components/makeMutationForm/makeMutationForm';
import MediumPill from 'components/MediumPill/MediumPill';
import NoEntitiesMessage from 'components/NoEntitiesMessage/NoEntitiesMessage';
import Page from 'components/Page';
import ResetButton from 'components/ResetButton/ResetButton';
import S3Image from 'components/S3Image';
import ScrollableBox from 'components/ScrollableBox/ScrollableBox';
import SearchInput from 'components/SearchInput';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import React from 'react';
import { toast } from 'react-toastify';
import { routeMap, useNavigate } from 'routeMap';
import {
  useTextInput,
  useWrappedUrqlMutation,
  validateMutationForm,
} from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import PreventLeavingFormIfTouched from 'toolbox/src/components/PreventLeavingFormIfTouched/PreventLeavingFormIfTouched';
import Stack from 'toolbox/src/components/Stack/Stack';
import { useAddOrganisationMutation } from './mutations/AddOrganisation.generated';
import { useUpdateOrgLiquidityLimitsMutation } from './OrgLiquidityLimits/UpdateOrgLiquidityLimits.generated';
import { useUpdateOrganisationMutation } from './mutations/UpdateOrganisation.generated';
import OrgImageUploader from './OrgImageUploader';
import OrgReports from './OrgReports';
import OrgUsers from './OrgUsers';
import { useGetOrganisationQuery } from './queries/GetOrganisation.generated';
import { OrgLiquidityLimits } from './OrgLiquidityLimits/OrgLiquidityLimits';
import { OrgExposureLimits } from './OrgExposureLimits/OrgExposureLimits';
import { useUpdateOrganisationDemoDashboardMutation } from './UpdateOrganisationDemoDashboard.generated';

const OrganisationForm = makeMutationForm<{
  name: string;
  short_name: string;
  demo_dashboard: boolean;
}>({
  config: {
    name: {
      type: 'text',
    },
    short_name: {
      type: 'text',
    },
    demo_dashboard: {
      type: 'checkbox',
    },
  },
  validate: (values) => {
    if (values.short_name?.length !== 4) {
      return {
        short_name:
          'You must provide a 4-letter, all-caps shortcode to identify this organisation.',
      };
    }
    return validateMutationForm({
      config: {
        name: ['required'],
        short_name: ['required'],
      },
      values,
    });
  },
});

const Organisation = ({
  id,
}: {
  // If id is null, we're in the new organisation form
  id: number | null;
}) => {
  const navigate = useNavigate();

  const searchBusinessUnit = useTextInput();

  const [result] = useGetOrganisationQuery({
    variables: {
      id: id as number,
      businessUnitSearch: `%${searchBusinessUnit.throttledValue}%`,
    },
    pause: !id,
  });

  if (result?.error) Bugsnag.notify(result?.error);

  const addDefaultLiquidityLimits = useWrappedUrqlMutation(
    useUpdateOrgLiquidityLimitsMutation,
    {
      onSuccess: () => {},
      onError: (error) => {
        toast.error(`Could not add default limits.`);
        console.warn(error);
      },
    },
  );

  const addOrganisation = useWrappedUrqlMutation(useAddOrganisationMutation, {
    onSuccess: (result) => {
      toast.info('Added organisation successfully.');
      addDefaultLiquidityLimits.dispatch({
        objects: [
          {
            organisation_id: result?.insert_organisations_one?.id,
            upper_limit_pct: 100,
            lower_limit_pct: 0,
            notice_days_gte: 0,
            notice_days_lt: 1,
          },
          {
            organisation_id: result?.insert_organisations_one?.id,
            upper_limit_pct: 100,
            lower_limit_pct: 0,
            notice_days_gte: 1,
            notice_days_lt: 8,
          },
          {
            organisation_id: result?.insert_organisations_one?.id,
            upper_limit_pct: 100,
            lower_limit_pct: 0,
            notice_days_gte: 8,
            notice_days_lt: 36,
          },
          {
            organisation_id: result?.insert_organisations_one?.id,
            upper_limit_pct: 100,
            lower_limit_pct: 0,
            notice_days_gte: 36,
            notice_days_lt: 96,
          },
          {
            organisation_id: result?.insert_organisations_one?.id,
            upper_limit_pct: 100,
            lower_limit_pct: 0,
            notice_days_gte: 96,
            notice_days_lt: -1,
          },
        ],
      });
      navigate.viewOrgById({
        params: {
          id: String(result?.insert_organisations_one?.id),
        },
      });
    },
    onError: (error) => {
      toast.error(`Could not add organisation.`);
      console.warn(error);
    },
  });

  const updateOrganisation = useWrappedUrqlMutation(
    useUpdateOrganisationMutation,
    {
      onSuccess: (result) => {
        toast.info(
          `Updated ${result.update_organisations.returning?.[0].name} sucessfully.`,
        );
      },
      onError: (error) => {
        toast.error(`Could not update organisation.`);
        console.warn(error);
      },
    },
  );

  const organisation = result.data?.organisations_by_pk;

  const saveChanges = async ({
    name,
    short_name,
    demo_dashboard,
  }: {
    name: string;
    short_name: string;
    demo_dashboard: boolean;
  }) => {
    if (!organisation) {
      await addOrganisation.dispatch({
        name,
        short_name,
        demo_dashboard,
      });
    } else {
      await updateOrganisation.dispatch({
        orgId: id,
        modification: {
          name,
          short_name,
          demo_dashboard,
        },
      });
    }
  };

  const changeDemoDashboard = useWrappedUrqlMutation(
    useUpdateOrganisationDemoDashboardMutation,
    {
      onError: () => {
        toast.error('Could not update this setting.');
      },
      onSuccess: () => {
        toast.success('Updated successfully.');
      },
    },
  );

  const removeOrgImage = async () => {
    try {
      const updateOrgResult = await updateOrganisation.dispatch({
        orgId: id,
        modification: { image_upload_id: null },
      });
      console.log(updateOrgResult);
    } catch (err) {
      console.warn(err);
    }
  };

  if (!organisation && result.fetching) {
    return null;
  }

  return (
    <Page
      title={organisation?.name || 'New Organisation'}
      icon={
        <OfficeIcon className="inline w-10 h-10 mr-2 -mb-1 text-gray-500" />
      }
    >
      <OrganisationForm.Wrapper
        initialValues={organisation || { demo_dashboard: true }}
        onSubmit={saveChanges}
      >
        <div className="card">
          <div className="cardBody">
            <aside>
              <h2 className="mb-6 text-lg font-semibold text-gray-800">
                Organisation Info
              </h2>

              {organisation?.image?.s3_key && (
                <div className="relative flex items-center mb-6 overflow-hidden bg-white rounded-lg">
                  <ClearIcon
                    className="absolute top-0 right-0 w-6 h-6 mt-1 mr-1 text-red-500 bg-red-100 border border-red-400 rounded-full cursor-pointer hover:text-white hover:bg-red-600"
                    onClick={() => removeOrgImage()}
                  />
                  <S3Image s3_key={organisation?.image?.s3_key} className="" />
                </div>
              )}

              {organisation?.id && (
                <OrgImageUploader orgId={organisation?.id} />
              )}
            </aside>
            <main>
              <OrganisationForm.Inputs.name label="Name" />
              <OrganisationForm.Inputs.short_name label="4-Letter Code" />
              <OrganisationForm.Inputs.demo_dashboard label="Show demo dashboard?" />
            </main>
          </div>
          <CardFooter>
            <Flex mx={3}>
              <ResetButton>Undo Changes</ResetButton>
              <SubmitButton disableIfNoInputsTouched>Save</SubmitButton>
            </Flex>
          </CardFooter>
        </div>
        <PreventLeavingFormIfTouched />
      </OrganisationForm.Wrapper>
      {organisation && (
        <CardWithAside
          title="Business Units"
          aside={
            <Stack>
              <SearchInput
                placeholder="Filter"
                {...searchBusinessUnit.inputProps}
              />
              <p className="text-sm">
                Business units which are contained within this organisation.
              </p>
            </Stack>
          }
          footer={
            <Button
              onClick={() =>
                navigate.createBusinessUnit({
                  search: {
                    orgId: id,
                  },
                })
              }
            >
              Add Business Unit
            </Button>
          }
        >
          <ScrollableBox>
            {organisation.business_units.map((business_unit) => (
              <MediumPill
                title={business_unit.name}
                Icon={OfficeIcon}
                to={routeMap.viewBusinessUnit({
                  params: { id: business_unit.id },
                })}
                key={business_unit.id}
              >
                <span className="mr-3 text-xs">
                  {business_unit.currency_books_aggregate?.aggregate?.count}{' '}
                  currency books
                </span>
              </MediumPill>
            ))}
            {!organisation.business_units.length &&
              !searchBusinessUnit.throttledValue && (
                <NoEntitiesMessage>
                  There are no business units associated with this organisation.
                </NoEntitiesMessage>
              )}
          </ScrollableBox>
        </CardWithAside>
      )}

      {organisation && <OrgReports organisation={organisation} />}
      {organisation && <OrgExposureLimits organisation={organisation} />}
      {organisation && <OrgLiquidityLimits organisation_id={id} />}
      {organisation && <OrgUsers organisation={organisation} />}
    </Page>
  );
};

export const NoOrganisation = () => (
  <Page title="">
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <OfficeIcon className="w-full h-auto max-w-sm text-gray-400" />
      <h2 className="text-2xl font-semibold text-gray-400">
        No Organisation Selected
      </h2>
    </div>
  </Page>
);

export default Organisation;
